/* eslint-disable no-unused-expressions */
import 'react-leaflet-markercluster/dist/styles.min.css';
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map as LeafletMap, Popup } from 'react-leaflet';
import Basemap from './Basemap';
import { MIN_MAP_ZOOM, MAX_MAP_ZOOM, clampMapZoom } from './zoomBounds';
import { mapNoTip } from '../../../services/FundedTips';
import Marker from 'react-leaflet-enhanced-marker';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import useInterval from '@use-it/interval';
import { DriverMarker } from '../../units/DriverMarker/DriverMarker';
import { RestaurantMarker } from '../../units/RestaurantMarker/RestaurantMarker';
import { OrderPickupMarker } from '../../units/OrderPickupMarker/OrderPickupMarker';
import { useMapHovering } from './Map.hooks';
import { useHoverContext } from '../../../context/HoverContext';
import { useMapContext } from '../../../context/MapContext';
import Coverage from '../Coverage/Coverage';
import { MapControlsBridge, useMapControls } from '../MapControls/MapControlsContext';

export const shouldBeDimmed = (id, hoverId) => hoverId && hoverId !== id;

function MarkerIcon({ markerType, status, text, tempClosed, isDimmed, order }) {
  if (markerType === 'restaurant') {
    return <RestaurantMarker name={text} tempClosed={tempClosed} />;
  }
  if (markerType === 'order') {
    return <OrderPickupMarker displayId={text} order={order} />;
  }
  return <DriverMarker status={status} text={text} isDimmed={isDimmed} />;
}

MarkerIcon.propTypes = {
  order: PropTypes.object,
  markerType: PropTypes.string,
  status: PropTypes.string,
  text: PropTypes.string,
  tempClosed: PropTypes.bool,
  isDimmed: PropTypes.bool,
};

function MarkerItem({
  id,
  onClick,
  status,
  text,
  latitude,
  longitude,
  markerType,
  tempClosed,
  order,
}) {
  const { hoverId } = useHoverContext();
  const isDimmed = useMemo(() => shouldBeDimmed(id, hoverId), [id, hoverId]);

  return (
    <Marker
      data-test-id="OKM7LHz615XhM-WhPIBuZ"
      onClick={onClick}
      key={id}
      icon={
        <MarkerIcon
          markerType={markerType}
          status={status}
          text={text}
          tempClosed={tempClosed}
          isDimmed={isDimmed}
          order={order}
        />
      }
      position={[latitude, longitude]}
    >
      {markerType === 'order' && order && (
        <Popup>
          <div>
            <strong>Order #{order.displayId || order.id}</strong>
            <div>{order.orderStatusName}</div>
            {mapNoTip(order) && <div>No Tip</div>}
          </div>
        </Popup>
      )}
    </Marker>
  );
}

MarkerItem.propTypes = {
  order: PropTypes.object,
  markerType: PropTypes.string,
  status: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  text: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  onClick: PropTypes.func,
  tempClosed: PropTypes.bool,
};

const NonMemoMarkersList = ({ markers }) =>
  markers?.map((markerItem) => (
    <MarkerItem key={markerItem.id} {...markerItem} />
  ));

const MarkersList = React.memo(NonMemoMarkersList);

MarkersList.propTypes = {
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      markerType: PropTypes.string,
      status: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      text: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      onClick: PropTypes.func,
    })
  ),
};

export const invalidateSize = (map) =>
  setTimeout(() => {
    map?.invalidateSize();
  }, 400);

export function Map({ mapProps, mapKey, groupMarkers }) {
  const controls = useMapControls();
  const { hoverId } = useHoverContext();

  const { mapRef, groupRef, initialPosition, setInitialPosition, markers } =
    useMapContext();

  useMapHovering({
    mapRef,
    hoverId,
  });

  useEffect(() => {
    const timeout = invalidateSize(mapRef?.current?.leafletElement);
    return () => clearTimeout(timeout);
  }, [mapRef]);

  useInterval(() => {
    const map = mapRef?.current?.leafletElement;
    if (map)
      setInitialPosition({ center: map.getCenter(), zoom: map.getZoom() });
  }, 2000);

  const firstPosition = useMemo(() => initialPosition, []); // eslint-disable-line

  return (
    <LeafletMap
      key={mapKey}
      preferCanvas
      center={firstPosition.center}
      ref={mapRef}
      zoomControl={false}
      {...mapProps}
      // These provider constraints must not be overridden by mapProps.
      minZoom={MIN_MAP_ZOOM}
      maxZoom={MAX_MAP_ZOOM}
      zoom={clampMapZoom(mapProps?.zoom ?? firstPosition.zoom)}
    >
      <Basemap />
      {controls ? <MapControlsBridge /> : <Coverage />}

      {groupMarkers ? (
        <MarkerClusterGroup ref={groupRef} maxClusterRadius={100}>
          <MarkersList markers={markers} />
        </MarkerClusterGroup>
      ) : (
        <MarkerClusterGroup
          ref={groupRef}
          maxClusterRadius={100}
          disableClusteringAtZoom
        >
          <MarkersList markers={markers} />
        </MarkerClusterGroup>
      )}
    </LeafletMap>
  );
}

Map.propTypes = {
  mapProps: PropTypes.any,
  mapKey: PropTypes.string,
  groupMarkers: PropTypes.bool,
};

Map.defaultProps = {
  mapProps: {},
  groupMarkers: true,
};
